/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/Dashboard.tsx — DEMO VERSION 2026-05-21
 * ----------------------------------------------------------------------------
 * Dashboard polido para demo executiva.
 *
 * Layout:
 *  - Hero header com tenant + greeting + horário
 *  - 4 KPI cards animados (trend indicators)
 *  - Throughput chart (area gradient, 7 dias)
 *  - Module catalog showcase (20 módulos, 339 primitivas)
 *  - Recent activity feed (auto-refresh visual)
 *
 * Dados:
 *  - Tenta /api/metrics/dashboard
 *  - Fallback para mock realista (demo-grade)
 * ========================================================================== */

import React, { useMemo, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/store/authStore'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import {
  Package,
  TrendingUp,
  TrendingDown,
  Users,
  Truck,
  AlertCircle,
  CheckCircle2,
  Clock,
  Activity,
  Scan,
  MapPin,
  ArrowUpRight,
  Zap,
  type LucideIcon,
} from 'lucide-react'

/* ============================================================================
   Types
   ========================================================================== */

type Kpi = {
  label: string
  value: string | number
  delta?: number // percentage; positive=up, negative=down. Ausente nos dados reais (sem histórico).
  deltaLabel?: string
  icon: LucideIcon
  iconBg: string
  iconColor: string
}

type ActivityItem = {
  id: string
  icon: LucideIcon
  title: string
  subtitle: string
  time: string
  type: 'success' | 'warning' | 'info' | 'pending'
}

/* ============================================================================
   Mock data (substituído por dados reais quando endpoint /api/metrics existir)
   ========================================================================== */

const MOCK_KPIS: Kpi[] = [
  {
    label: 'Stock Coverage',
    value: '87%',
    delta: 2.4,
    deltaLabel: 'vs semana passada',
    icon: Package,
    iconBg: 'bg-blue-500/15',
    iconColor: 'text-blue-400',
  },
  {
    label: 'Picks Today',
    value: '1,247',
    delta: 12.3,
    deltaLabel: 'vs ontem',
    icon: Scan,
    iconBg: 'bg-emerald-500/15',
    iconColor: 'text-emerald-400',
  },
  {
    label: 'Pending Receipts',
    value: 12,
    delta: -8.5,
    deltaLabel: 'vs ontem',
    icon: Truck,
    iconBg: 'bg-amber-500/15',
    iconColor: 'text-amber-400',
  },
  {
    label: 'Active Operators',
    value: 23,
    delta: 4.2,
    deltaLabel: 'em turno agora',
    icon: Users,
    iconBg: 'bg-violet-500/15',
    iconColor: 'text-violet-400',
  },
]

const MOCK_THROUGHPUT = [
  { day: 'Seg', picks: 980, receipts: 145 },
  { day: 'Ter', picks: 1120, receipts: 178 },
  { day: 'Qua', picks: 1340, receipts: 156 },
  { day: 'Qui', picks: 1180, receipts: 210 },
  { day: 'Sex', picks: 1450, receipts: 198 },
  { day: 'Sáb', picks: 890, receipts: 92 },
  { day: 'Hoje', picks: 1247, receipts: 167 },
]

const MOCK_ACTIVITY: ActivityItem[] = [
  {
    id: '1',
    icon: CheckCircle2,
    title: 'Wave W-20260521-003 concluída',
    subtitle: '47 picks · 12 operadores · tempo médio 2.3 min',
    time: 'agora',
    type: 'success',
  },
  {
    id: '2',
    icon: Truck,
    title: 'Receção R-2026-0852 confirmada',
    subtitle: 'Supplier Acme Ltd · 24 pallets · doca DK-03',
    time: '5 min',
    type: 'info',
  },
  {
    id: '3',
    icon: AlertCircle,
    title: 'Stock low: ABC-002-M abaixo do mínimo',
    subtitle: 'Location A-01-03 · 5 un disponíveis · reposição sugerida',
    time: '12 min',
    type: 'warning',
  },
  {
    id: '4',
    icon: Package,
    title: 'Putaway concluído: PT-2026-1247',
    subtitle: 'Operador Manuel C. · location B-04-01 · 14 itens',
    time: '18 min',
    type: 'success',
  },
  {
    id: '5',
    icon: Clock,
    title: 'TimeSlot TS-0521-14:30 agendado',
    subtitle: 'Carrier XYZ Logistics · doca outbound DK-07',
    time: '25 min',
    type: 'pending',
  },
]

const MODULE_HIGHLIGHTS = [
  { code: 'SC', name: 'Scan', count: 15, color: 'bg-blue-500' },
  { code: 'MV', name: 'Movements', count: 21, color: 'bg-cyan-500' },
  { code: 'WK', name: 'Workforce', count: 26, color: 'bg-violet-500' },
  { code: 'CP', name: 'Copack', count: 27, color: 'bg-emerald-500' },
  { code: 'BN', name: 'Locations', count: 17, color: 'bg-amber-500' },
  { code: 'ID', name: 'Identifier', count: 24, color: 'bg-rose-500' },
  { code: 'YS', name: 'Yard', count: 18, color: 'bg-indigo-500' },
  { code: 'PT', name: 'Partners', count: 16, color: 'bg-teal-500' },
]

/* ============================================================================
   Indicadores reais (ADR-0035) — payload de GET /api/metrics/dashboard
   ========================================================================== */

type DashItem = {
  code: string
  name: string
  category: string
  unit: string
  value: number | null
  position: number
  pinned: boolean
}
type DashboardResponse = { company_id: number; items: DashItem[] }

const CATEGORY_STYLE: Record<
  string,
  { icon: LucideIcon; iconBg: string; iconColor: string }
> = {
  OPERACAO: {
    icon: Users,
    iconBg: 'bg-violet-500/15',
    iconColor: 'text-violet-400',
  },
  STOCK: {
    icon: Package,
    iconBg: 'bg-blue-500/15',
    iconColor: 'text-blue-400',
  },
  ENDERECOS: {
    icon: MapPin,
    iconBg: 'bg-amber-500/15',
    iconColor: 'text-amber-400',
  },
  ENCOMENDAS: {
    icon: Scan,
    iconBg: 'bg-emerald-500/15',
    iconColor: 'text-emerald-400',
  },
  YARD: {
    icon: Truck,
    iconBg: 'bg-indigo-500/15',
    iconColor: 'text-indigo-400',
  },
  COMPRAS: {
    icon: Truck,
    iconBg: 'bg-teal-500/15',
    iconColor: 'text-teal-400',
  },
}
const DEFAULT_STYLE = {
  icon: Activity,
  iconBg: 'bg-slate-500/15',
  iconColor: 'text-slate-400',
}
const CATEGORY_LABEL: Record<string, string> = {
  OPERACAO: 'Operação',
  STOCK: 'Stock',
  ENDERECOS: 'Endereços',
  ENCOMENDAS: 'Encomendas',
  YARD: 'Yard',
  COMPRAS: 'Compras',
}

// Respeita o unit: PERCENT -> %, DURATION -> tempo, COUNT -> número. null -> "—".
function formatIndicator(value: number | null, unit: string): string {
  if (value === null || value === undefined) return '—'
  if (unit === 'PERCENT') return `${value}%`
  if (unit === 'DURATION')
    return value < 90
      ? `${Math.round(value)} s`
      : `${(value / 60).toFixed(1)} min`
  return new Intl.NumberFormat('pt-PT').format(value)
}

function toKpi(it: DashItem): Kpi {
  const style = CATEGORY_STYLE[it.category] ?? DEFAULT_STYLE
  return {
    label: it.name,
    value: formatIndicator(it.value, it.unit),
    deltaLabel: CATEGORY_LABEL[it.category] ?? it.category,
    icon: style.icon,
    iconBg: style.iconBg,
    iconColor: style.iconColor,
  }
}

/* ============================================================================
   Sub-components
   ========================================================================== */

function KpiCard({ kpi }: { kpi: Kpi }) {
  const Icon = kpi.icon
  const hasDelta = typeof kpi.delta === 'number'
  const positive = (kpi.delta ?? 0) >= 0
  const TrendIcon = positive ? TrendingUp : TrendingDown
  const trendColor = positive ? 'text-emerald-400' : 'text-rose-400'

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-800/60 bg-slate-900/40 p-5 backdrop-blur-sm hover:border-slate-700/80 hover:bg-slate-900/60 transition-all duration-200 group">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
            {kpi.label}
          </div>
          <div className="mt-2 text-3xl font-bold text-white tabular-nums">
            {kpi.value}
          </div>
          {hasDelta && (
            <div
              className={`mt-2 flex items-center gap-1 text-xs font-medium ${trendColor}`}
            >
              <TrendIcon size={12} />
              <span>
                {positive ? '+' : ''}
                {kpi.delta}%
              </span>
              {kpi.deltaLabel && (
                <span className="text-slate-500 font-normal">
                  · {kpi.deltaLabel}
                </span>
              )}
            </div>
          )}
          {!hasDelta && kpi.deltaLabel && (
            <div className="mt-2 text-xs font-medium text-slate-500">
              {kpi.deltaLabel}
            </div>
          )}
        </div>
        <div
          className={`shrink-0 ${kpi.iconBg} rounded-lg p-2.5 transition-transform group-hover:scale-110`}
        >
          <Icon size={18} className={kpi.iconColor} />
        </div>
      </div>
      {/* subtle accent bar */}
      <div
        className={`absolute bottom-0 left-0 h-0.5 ${kpi.iconBg.replace('/15', '/40')} w-full opacity-0 group-hover:opacity-100 transition-opacity`}
      />
    </div>
  )
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const Icon = item.icon
  const colorMap = {
    success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    warning: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    info: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    pending: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
  }
  return (
    <div className="flex items-start gap-3 py-3 px-3 rounded-lg hover:bg-slate-800/30 transition-colors">
      <div
        className={`shrink-0 mt-0.5 rounded-md border p-1.5 ${colorMap[item.type]}`}
      >
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-200 truncate">
          {item.title}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {item.subtitle}
        </div>
      </div>
      <div className="shrink-0 text-[11px] text-slate-500 mt-0.5 tabular-nums">
        {item.time}
      </div>
    </div>
  )
}

/* ============================================================================
   Main Component
   ========================================================================== */

export default function Dashboard() {
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(t)
  }, [])

  // Try real endpoint, fall back to mock
  const { data: _metrics } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: async () => {
      try {
        const r = await api.get('/api/metrics/dashboard')
        return r.data
      } catch {
        return null
      }
    },
    staleTime: 30_000,
  })

  // ADR-0035: dados reais do endpoint; fallback gracioso ao mock se vier vazio/falhar.
  const kpis: Kpi[] = useMemo(() => {
    const items = (_metrics as DashboardResponse | null)?.items
    if (!items || items.length === 0) return MOCK_KPIS
    return [...items]
      .sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) || a.position - b.position,
      )
      .map(toKpi)
  }, [_metrics])
  const throughput = MOCK_THROUGHPUT
  const activity = MOCK_ACTIVITY

  const user = useAuthStore((s) => s.user)
  const nome = user?.first_name || user?.username || ''

  const greeting = useMemo(() => {
    const h = now.getHours()
    if (h < 12) return 'Bom dia'
    if (h < 19) return 'Boa tarde'
    return 'Boa noite'
  }, [now])

  const dateStr = now.toLocaleDateString('pt-PT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div className="px-4 lg:px-6 py-5 max-w-[1600px] mx-auto space-y-5">
      {/* Hero header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
            {greeting}
            {nome ? `, ${nome}` : ''}
          </h1>
          <p className="text-sm text-slate-400 mt-1 capitalize">{dateStr}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-900/40 border border-slate-800/60 rounded-lg px-3 py-2">
          <Activity size={14} className="text-emerald-400 animate-pulse" />
          <span>Sistema operacional</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-300 font-medium">99.97% uptime</span>
        </div>
      </header>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
      </div>

      {/* Main row: Chart + Module catalog */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Throughput chart (2/3) */}
        <div className="lg:col-span-2 rounded-xl border border-slate-800/60 bg-slate-900/40 p-5 backdrop-blur-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-white">
                Throughput · últimos 7 dias
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Picks · Receções diários
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-blue-400" />
                <span className="text-slate-400">Picks</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="text-slate-400">Receções</span>
              </div>
            </div>
          </div>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <AreaChart
                data={throughput}
                margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
              >
                <defs>
                  <linearGradient
                    id="picksGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="receiptsGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(148,163,184,0.1)"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(15,23,42,0.96)',
                    border: '1px solid rgba(148,163,184,0.2)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
                  itemStyle={{ color: '#e2e8f0' }}
                />
                <Area
                  type="monotone"
                  dataKey="picks"
                  stroke="#60a5fa"
                  strokeWidth={2}
                  fill="url(#picksGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="receipts"
                  stroke="#34d399"
                  strokeWidth={2}
                  fill="url(#receiptsGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Module catalog showcase (1/3) — STORYTELLING WOW */}
        <div className="rounded-xl border border-slate-800/60 bg-gradient-to-br from-slate-900/60 to-slate-900/30 p-5 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-white">
              Engineering Excellence
            </h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/30">
              <Zap size={10} className="text-cyan-300" />
              <span className="text-[10px] font-bold tracking-wider text-cyan-200">
                CORE
              </span>
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Primitivas técnicas catalogadas
          </p>

          {/* Big number */}
          <div className="text-center py-2">
            <div className="text-5xl font-bold tabular-nums bg-gradient-to-br from-blue-300 to-cyan-300 bg-clip-text text-transparent">
              339
            </div>
            <div className="text-xs text-slate-400 mt-1">
              primitivas em 20 módulos
            </div>
          </div>

          {/* Module pills */}
          <div className="mt-4 grid grid-cols-2 gap-1.5">
            {MODULE_HIGHLIGHTS.map((m) => (
              <div
                key={m.code}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-800/40 border border-slate-800/60 hover:border-slate-700 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`h-1.5 w-1.5 rounded-full ${m.color}`} />
                  <span className="font-mono text-[10px] font-bold text-slate-300">
                    {m.code}
                  </span>
                  <span className="text-xs text-slate-400 truncate">
                    {m.name}
                  </span>
                </div>
                <span className="text-[10px] tabular-nums text-slate-500">
                  {m.count}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] text-slate-500 text-center">
            Rigor industrial · Agilidade software
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="rounded-xl border border-slate-800/60 bg-slate-900/40 p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-white">
              Actividade Recente
            </h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold tracking-wider text-emerald-200">
                LIVE
              </span>
            </span>
          </div>
          <button className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 transition-colors">
            Ver tudo
            <ArrowUpRight size={12} />
          </button>
        </div>
        <div className="space-y-0.5">
          {activity.map((item) => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </div>
      </div>
    </div>
  )
}
